import { useMemo } from 'react';

import { useQueryLight, type UseQueryLightError } from '@atlaskit/teams-client';
import {
	useExitedTeams,
	useSite,
	useTeamVisibility,
	useV3OrgIdStatus,
} from '@atlassian/ptc-common';
import { getConfig } from '@atlassian/teams-common/context-config';

import TeamSearchQueryV2 from './TeamSearchQueryV2.graphql';
import {
	sortBy,
	type TeamSearchProps,
	type TeamSearchState,
	type TeamSearchV2Response,
	type TeamSort,
} from './types';
import { filterExcludedMessages, fromV2ToTeamSearchViewFormat, ORG_ARI_PREFIX } from './utils';

const excludeKnownGraphQLErrors = (error: UseQueryLightError): UseQueryLightError | undefined => {
	if (
		error.message?.includes('User does not have access to the org') ||
		error.graphQLErrors?.some((e) => e.extensions?.statusCode === 403)
	) {
		// Escapes from this error as discussed in https://atlassian.slack.com/archives/C04939SL1J5/p1675215336793559
		return undefined;
	} else if (error.graphQLErrors) {
		const filteredError = {
			...error, // TODO PR Question - Is duplicating an error via spreading OK? (Not really an "Error" after that)
			graphQLErrors: filterExcludedMessages(error.graphQLErrors),
		};

		// If there's no actual errors left, then discard the error object
		const noErrorsLeft =
			filteredError.networkError == null && filteredError.graphQLErrors.length === 0;
		return noErrorsLeft ? undefined : filteredError;
	}
	return error;
};

function orgIdWithARI(orgId?: string) {
	return orgId?.startsWith(ORG_ARI_PREFIX) ? orgId : `${ORG_ARI_PREFIX}${orgId}`;
}

type Variables = {
	first?: number;
	memberIds?: string[];
	query?: string;
	organizationId: string;
	after?: string;
	sortBy?: string | null | TeamSort[];
	siteId: string;
	showEmptyTeams: boolean;
};

export const useTeamSearchQuery = (props: TeamSearchProps): TeamSearchState => {
	const { cursor, first, memberIds, query, batchSize, skip: providedSkip } = props;
	const { orgId } = useV3OrgIdStatus();
	const [exitedTeams] = useExitedTeams();
	const {
		isLoading: isTeamsVisibilityLoading,
		permissions: { canViewTeams },
	} = useTeamVisibility();

	const [{ cloudId }] = useSite();

	const { data, loading, error, fetchMore } = useQueryLight<Variables, TeamSearchV2Response>(
		TeamSearchQueryV2,
		{
			customHeaders: {
				'X-ExperimentalApi': 'team-members-beta',
				'atl-client-name': process.env._PACKAGE_NAME_,
				'atl-client-version': process.env._PACKAGE_VERSION_,
			},
			operationName: 'TeamSearchV2',
			serviceUrl: `${getConfig().stargateRoot}/graphql`,
			variables: {
				first,
				memberIds,
				query,
				organizationId: orgIdWithARI(orgId),
				after: cursor,
				sortBy: query ? null : sortBy,
				siteId: cloudId,
				showEmptyTeams: true,
			},
			skip:
				providedSkip ||
				!orgId ||
				/**
				 * Don't make the call when
				 * 1. the permissions are currently loading
				 * 2. the permissions have loaded but the user doesn't have permission to view teams
				 */
				!canViewTeams ||
				isTeamsVisibilityLoading,
		},
	);

	const filteredError = error && excludeKnownGraphQLErrors(error);

	const formattedTeams = useMemo(() => {
		if (!data?.team || filteredError) {
			// GraphQL queries can have a partial failure, where some data is returned
			// but not all of it. By default, Apollo will discard any partial data if
			// there's an error:
			// https://www.apollographql.com/docs/react/data/error-handling/#partial-data-with-resolver-errors
			//
			// In PTC-6588, we wanted to be able to support partial data for the case
			// where a team has been deleted, so we set the error policy to 'all'.
			// Otherwise, the whole page fails to load because of one bad team.
			// However, most of the app wasn't written to handle partial data, so if
			// there are any errors, we return nothing.
			return [];
		}

		return fromV2ToTeamSearchViewFormat(data.team?.teamSearch?.nodes || []);
	}, [data, filteredError]);

	const loadMore = () => {
		void fetchMore({
			variables: {
				after: data?.team?.teamSearch?.pageInfo?.endCursor,
				first: batchSize || first,
			},
			handleDataMerge: (prevResult, fetchMoreResult) => {
				if (!fetchMoreResult) {
					return prevResult;
				}
				const prevTeams = prevResult?.team?.teamSearch?.nodes || [];
				const newTeams = fetchMoreResult?.team?.teamSearch?.nodes || [];

				return {
					...fetchMoreResult,
					team: {
						...fetchMoreResult.team,
						teamSearch: {
							...fetchMoreResult.team?.teamSearch,
							nodes: [...prevTeams, ...newTeams],
						},
					},
				};
			},
		});
	};

	const filteredTeams = useMemo(
		() => formattedTeams.filter((team) => !exitedTeams.has(team.id)),
		[formattedTeams, exitedTeams],
	);

	return {
		isLoading: loading,
		teams: filteredTeams,
		error: filteredError,
		loadMore,
		hasMore: !!data?.team?.teamSearch?.pageInfo?.hasNextPage,
	};
};
